import React, { useState, useEffect } from 'react';
import './TutorialAdmin.css'; // Reusing styles since they are identical

const AdvancedLearningAdmin = ({ onBack }) => {
  const [videos, setVideos] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [category, setCategory] = useState('Technical Analysis');
  const [level, setLevel] = useState('Advanced');
  const [thumbnail, setThumbnail] = useState('');
  const [duration, setDuration] = useState('');
  const [views, setViews] = useState(0);

  const categories = [
    'Technical Analysis', 
    'Fundamental Analysis', 
    'Options Trading', 
    'Risk Management', 
    'Trading Psychology', 
    'Market Basics', 
    'Advanced Strategies'
  ];

  const levels = ['Beginner', 'Intermediate', 'Advanced'];

  useEffect(() => {
    const saved = localStorage.getItem('stocks4u_advanced_learning');
    if (saved) {
      setVideos(JSON.parse(saved));
    }
  }, []);

  const saveVideos = (updated) => {
    localStorage.setItem('stocks4u_advanced_learning', JSON.stringify(updated));
    setVideos(updated);
  };

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnail(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoUrl(`/videos/${file.name}`);
      alert(`Video selected: ${file.name}\n\nIMPORTANT: Move this file to "public/videos/${file.name}" for it to play.`);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!title || !videoUrl || !duration) {
      alert('Title, Video URL, and Duration are required!');
      return;
    }

    const videoData = {
      id: isEditing ? editId : Date.now(),
      title,
      desc: description,
      videoUrl,
      category,
      level,
      thumbnail,
      duration,
      views: parseInt(views) || 0,
      timeAgo: 'Just now',
      color: getCategoryColor(category)
    };

    let updated;
    if (isEditing) {
      updated = videos.map(v => v.id === editId ? videoData : v);
    } else {
      updated = [...videos, videoData];
    }

    saveVideos(updated);
    resetForm();
    setShowForm(false);
    alert(isEditing ? 'Video updated successfully!' : 'Video posted successfully!');
  };

  const getCategoryColor = (cat) => {
    const colors = {
      'Technical Analysis': '#a855f7',
      'Options Trading': '#3b82f6',
      'Risk Management': '#10b981',
      'Market Basics': '#f59e0b',
      'Trading Psychology': '#ec4899',
      'Advanced Strategies': '#6366f1',
      'Fundamental Analysis': '#10b981'
    };
    return colors[cat] || '#8b5cf6';
  };

  const handleEdit = (video) => {
    setEditId(video.id);
    setTitle(video.title);
    setDescription(video.desc || '');
    setVideoUrl(video.videoUrl || '');
    setCategory(video.category || 'Technical Analysis');
    setLevel(video.level || 'Advanced');
    setThumbnail(video.thumbnail || '');
    setDuration(video.duration || '');
    setViews(video.views || 0);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      const updated = videos.filter(v => v.id !== id);
      saveVideos(updated);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setVideoUrl('');
    setCategory('Technical Analysis');
    setLevel('Advanced');
    setThumbnail('');
    setDuration('');
    setViews(0);
    setIsEditing(false);
    setEditId(null);
  };

  return (
    <div className="tut-admin-page">
      <header className="tut-admin-header">
        <div className="tut-admin-header-container">
          {onBack && (
            <button className="tut-admin-back-btn" onClick={onBack}>
              ← Back
            </button>
          )}
          <h1>Advanced Learning Admin Panel</h1>
          <button className="new-tut-btn" onClick={() => { resetForm(); setShowForm(true); }}>
            + New Video
          </button>
        </div>
      </header>

      <main className="tut-admin-main">
        {showForm && (
          <section className="tut-form-section">
            <h2>{isEditing ? 'Edit Learning Video' : 'Create New Learning Video'}</h2>
            <form className="tut-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Level</label>
                  <select value={level} onChange={(e) => setLevel(e.target.value)}>
                    {levels.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Duration * (e.g. 15:00)</label>
                  <input type="text" value={duration} onChange={(e) => setDuration(e.target.value)} required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Video URL / Path *</label>
                  <input type="text" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="/videos/filename.mp4 or YT Link" required />
                </div>
                <div className="form-group">
                  <label>Choose Local Video</label>
                  <input type="file" accept="video/*" onChange={handleVideoUpload} className="file-input" />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Thumbnail Image</label>
                  <input type="file" accept="image/*" onChange={handleThumbnailUpload} className="file-input" />
                  {thumbnail && (
                    <div className="image-preview">
                      <img src={thumbnail} alt="Preview" />
                      <button type="button" className="remove-image-btn" onClick={() => setThumbnail('')}>Remove</button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Initial Views</label>
                  <input type="number" value={views} onChange={(e) => setViews(e.target.value)} />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="save-btn">{isEditing ? 'Update Video' : 'Post Video'}</button>
              </div>
            </form>
          </section>
        )}

        <section className="tut-list-section">
          <h2>All Learning Videos ({videos.length})</h2>
          <div className="admin-tut-list">
            {videos.map(v => (
              <div key={v.id} className="admin-tut-card">
                <div className="admin-tut-info">
                  <div className="admin-tut-thumb">
                    {v.thumbnail ? <img src={v.thumbnail} alt="" /> : <div className="thumb-placeholder">Video</div>}
                  </div>
                  <div className="admin-tut-details">
                    <h3>{v.title}</h3>
                    <p>{v.desc}</p>
                    <div className="admin-tut-meta">
                      <span className="admin-tut-category">{v.category}</span>
                      <span className="admin-tut-duration">{v.level} • {v.duration}</span>
                      <span className="admin-tut-progress">{v.views} Views</span>
                    </div>
                  </div>
                </div>
                <div className="admin-tut-actions">
                  <button className="edit-btn" onClick={() => handleEdit(v)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDelete(v.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdvancedLearningAdmin;
